/**
 * Boat Anodes Persistence Module
 * Handles loading and saving anode assignments to the database
 */

/**
 * Check if there are unsaved anode changes
 * @returns {boolean} True if there are unsaved changes
 */
export function hasUnsavedAnodeChanges() {
    if (!window.anodeChangesTracking) return false;
    return Object.keys(window.anodeChangesTracking).length > 0;
}

/**
 * Prompt user about unsaved changes before clearing anodes
 * @returns {Promise<boolean>} True if user wants to proceed (discard changes), false to cancel
 */
export async function promptUnsavedAnodeChanges() {
    if (!hasUnsavedAnodeChanges()) {
        return true; // No changes, proceed
    }

    const message = 'You have unsaved anode changes for this boat.\n\n' +
                    'These changes will be lost if you select a different boat.\n\n' +
                    'Do you want to proceed without saving?';

    return confirm(message);
}

/**
 * Load boat anodes from database when a boat is selected
 * Auto-populates anode assignments for faster data entry
 *
 * @param {string} boatId - The boat UUID
 * @returns {Promise<void>}
 */
export async function loadBoatAnodes(boatId) {
    if (!boatId) {
        console.log('⚠️ [ANODE-PERSISTENCE] No boat ID provided, skipping anode load');
        return;
    }

    try {
        console.log(`📥 [ANODE-PERSISTENCE] Loading saved anodes for boat ${boatId}...`);

        // ALWAYS clear existing assignments when loading a new boat
        console.log('🧹 [ANODE-PERSISTENCE] Clearing previous boat anodes...');
        window.anodeAssignments = {};
        window.anodeChangesTracking = {}; // Clear change tracking

        // Query Supabase directly for boat anodes (with separate location/position columns)
        const { data: boatAnodes, error } = await window.supabaseClient
            .from('boat_anodes')
            .select('location, position, anode_catalog_id, anode_name, anode_price, last_condition_percent, is_active')
            .eq('boat_id', boatId)
            .eq('is_active', true);  // Only load active anodes

        if (error) {
            console.error('❌ [ANODE-PERSISTENCE] Error loading boat anodes:', error);
            // Still update UI even if error (to clear previous boat's anodes)
            if (typeof window.updateAnodeAssignmentsList === 'function') {
                window.updateAnodeAssignmentsList();
            }
            if (typeof window.updateLocationCounts === 'function') {
                window.updateLocationCounts();
            }
            return;
        }

        if (boatAnodes && boatAnodes.length > 0) {
            // Transform the data to match expected format
            const data = {
                success: true,
                anodes: boatAnodes.map(boatAnode => {
                    return {
                        location: boatAnode.location || 'unknown',
                        position: boatAnode.position || 'unknown',
                        anode_catalog_id: boatAnode.anode_catalog_id,
                        anode_name: boatAnode.anode_name || 'Unknown',
                        anode_price: boatAnode.anode_price || 0,
                        last_condition_percent: boatAnode.last_condition_percent || 100
                    };
                })
            };

            if (data.success && data.anodes && data.anodes.length > 0) {
            console.log(`✅ [ANODE-PERSISTENCE] Loaded ${data.anodes.length} saved anode(s):`);
            data.anodes.forEach((anode, index) => {
                console.log(`   ${index + 1}. ${anode.location} - ${anode.position}: ${anode.anode_name} (${anode.last_condition_percent}%)`);
            });

            // Populate anodeAssignments from saved data
            data.anodes.forEach((anode, index) => {
                const key = `${anode.location}-${anode.position}-${Date.now()}-${index}`;
                window.anodeAssignments[key] = {
                    location: anode.location,
                    position: anode.position,
                    condition: anode.last_condition_percent || 100,
                    status: 'inspected',
                    anode_id: anode.anode_catalog_id,
                    anode_name: anode.anode_name,
                    anode_price: anode.anode_price
                };
            });

            console.log(`📦 [ANODE-PERSISTENCE] window.anodeAssignments populated with ${Object.keys(window.anodeAssignments).length} entries`);

            // Fetch inventory status for loaded anodes
            const anodeCatalogIds = data.anodes.map(a => a.anode_catalog_id).filter(Boolean);
            if (anodeCatalogIds.length > 0 && typeof window.fetchAnodeInventoryStatus === 'function') {
                console.log(`📊 [ANODE-PERSISTENCE] Fetching inventory status for ${anodeCatalogIds.length} anode(s)...`);
                const inventoryStatuses = await window.fetchAnodeInventoryStatus(anodeCatalogIds, boatId);
                window.__anodeInventoryStatus = inventoryStatuses;
                console.log(`✅ [ANODE-PERSISTENCE] Inventory status fetched and stored in window.__anodeInventoryStatus`);
            } else {
                console.warn('⚠️ [ANODE-PERSISTENCE] window.fetchAnodeInventoryStatus() not available or no catalog IDs');
            }

            // Update UI if the function exists
            if (typeof window.updateAnodeAssignmentsList === 'function') {
                window.updateAnodeAssignmentsList();
                console.log('🔄 [ANODE-PERSISTENCE] UI updated via updateAnodeAssignmentsList()');
            } else {
                console.warn('⚠️ [ANODE-PERSISTENCE] window.updateAnodeAssignmentsList() not available');
            }
            if (typeof window.updateLocationCounts === 'function') {
                window.updateLocationCounts();
                console.log('🔄 [ANODE-PERSISTENCE] UI updated via updateLocationCounts()');
            } else {
                console.warn('⚠️ [ANODE-PERSISTENCE] window.updateLocationCounts() not available');
            }

            // Update wizard pricing/report to show loaded anodes
            if (typeof window.updateWizardPricing === 'function') {
                window.updateWizardPricing();
                console.log('🔄 [ANODE-PERSISTENCE] Report updated via updateWizardPricing()');
            } else {
                console.warn('⚠️ [ANODE-PERSISTENCE] window.updateWizardPricing() not available');
            }

            // Refresh anodes display in boat card
            if (typeof window.refreshAnodesDisplay === 'function') {
                window.refreshAnodesDisplay();
                console.log('🔄 [ANODE-PERSISTENCE] Anodes display refreshed in boat card');
            }

            console.log('✅ [ANODE-PERSISTENCE] Anode assignments loaded and UI updated successfully');
            }
        } else {
            console.log('ℹ️ [ANODE-PERSISTENCE] No saved anodes found for this boat');

            // Update UI to show empty state (clear previous boat's anodes from display)
            if (typeof window.updateAnodeAssignmentsList === 'function') {
                window.updateAnodeAssignmentsList();
                console.log('🔄 [ANODE-PERSISTENCE] UI cleared via updateAnodeAssignmentsList()');
            }
            if (typeof window.updateLocationCounts === 'function') {
                window.updateLocationCounts();
                console.log('🔄 [ANODE-PERSISTENCE] Location counts cleared via updateLocationCounts()');
            }
            if (typeof window.updateWizardPricing === 'function') {
                window.updateWizardPricing();
                console.log('🔄 [ANODE-PERSISTENCE] Report cleared via updateWizardPricing()');
            }
            if (typeof window.refreshAnodesDisplay === 'function') {
                window.refreshAnodesDisplay();
                console.log('🔄 [ANODE-PERSISTENCE] Anodes display cleared in boat card');
            }
        }
    } catch (error) {
        console.error('❌ [ANODE-PERSISTENCE] Error loading boat anodes:', error);
    }
}

/**
 * Save boat anodes to database after service is completed
 *
 * @param {string} boatId - The boat UUID
 * @param {Object} anodeAssignments - Object containing anode assignments
 * @returns {Promise<Object>} Response object with success status
 */
export async function saveBoatAnodes(boatId, anodeAssignments) {
    console.log(`💾 [ANODE-PERSISTENCE] saveBoatAnodes called with boatId: ${boatId}`);

    if (!boatId || !anodeAssignments || Object.keys(anodeAssignments).length === 0) {
        console.log('⚠️ [ANODE-PERSISTENCE] No boat ID or anode assignments to save');
        console.log(`   - boatId: ${boatId}`);
        console.log(`   - anodeAssignments: ${anodeAssignments ? Object.keys(anodeAssignments).length + ' entries' : 'null'}`);
        return { success: true, message: 'Nothing to save' };
    }

    try {
        console.log(`📋 [ANODE-PERSISTENCE] Processing ${Object.keys(anodeAssignments).length} anode assignments...`);

        const anodes = Object.values(anodeAssignments)
            .filter(anode => anode.status === 'replaced' || anode.anode_name)
            .map(anode => {
                return {
                    location: (anode.location || '').toLowerCase(),
                    position: (anode.position || '').toLowerCase(),
                    anode_catalog_id: anode.anode_id,
                    anode_name: anode.anode_name,
                    anode_price: anode.anode_price,
                    inventory_id: anode.inventory_id || null,
                    condition_percent: anode.condition || 100
                };
            });

        if (anodes.length === 0) {
            console.log('⚠️ [ANODE-PERSISTENCE] No anodes to save (none selected or named)');
            return { success: true, message: 'No anodes selected' };
        }

        console.log(`💾 [ANODE-PERSISTENCE] Saving ${anodes.length} anode(s) to boat ${boatId}:`);
        anodes.forEach((anode, index) => {
            console.log(`   ${index + 1}. ${anode.location} - ${anode.position}: ${anode.anode_name} (${anode.condition_percent}%)`);
        });

        const { data, error } = await window.supabaseClient.functions.invoke('boat-anodes', {
            body: { boatId, anodes }
        });

        if (error) {
            console.error('❌ [ANODE-PERSISTENCE] Error saving boat anodes:', error);
            return { success: false, error: error.message };
        }

        if (data && data.success) {
            console.log(`✅ [ANODE-PERSISTENCE] Boat anodes saved successfully: ${data.message}`);
            if (data.results) {
                data.results.forEach((result, index) => {
                    if (result.success) {
                        console.log(`   ✓ Anode ${index + 1} saved`);
                    } else {
                        console.error(`   ✗ Anode ${index + 1} failed:`, result);
                    }
                });
            }
            return data;
        } else {
            console.error('❌ [ANODE-PERSISTENCE] Failed to save boat anodes:', data?.error);
            return data || { success: false, error: 'Unknown error' };
        }
    } catch (error) {
        console.error('❌ [ANODE-PERSISTENCE] Error saving boat anodes:', error);
        return { success: false, error: error.message };
    }
}
